import { formatDateDisplay, formatQuantityTons, toDateInputValue } from "../hooks/useDataManagerOrdersUtils.js";
import {
  ORDER_ADVANCED_FILTER_CATALOG,
  getOrderReportFieldDisplayValue,
} from "./dataManagerOrdersCustomization.js";
import { resolveOrderDeliveredByDriverSummary } from "./dataManagerDeliveryDrivers.js";

export const ORDER_SEARCH_FIELD_KEYS = Object.freeze([
  "delivery_date",
  "order_id",
  "work_order_number",
  "client_name",
  "building",
  "coop",
  "loading_silo",
  "grain_code",
  "grain_type",
  "quantity_tons",
  "delivered_by_driver_name",
]);

const ORDER_ID_FIELD_KEY = "order_id";
const DELIVERY_DATE_FIELD_KEY = "delivery_date";
const QUANTITY_FIELD_KEY = "quantity_tons";

export const ORDER_SEARCH_MIN_QUERY_LENGTH = 3;

export function resolveActiveOrderSearchQuery(query) {
  const trimmedQuery = typeof query === "string" ? query.trim() : "";
  if (trimmedQuery.length < ORDER_SEARCH_MIN_QUERY_LENGTH) {
    return "";
  }

  return trimmedQuery;
}

export function isOrderSearchQueryActive(query) {
  return resolveActiveOrderSearchQuery(query).length > 0;
}

export function normalizeSearchText(value) {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

export function normalizeNumericToken(value) {
  const normalized = normalizeSearchText(String(value).replace(",", "."));
  if (!normalized) {
    return "";
  }

  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return normalized;
  }

  return String(parsed);
}

export function tokenizeQuery(query) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) {
    return [];
  }

  return normalizedQuery
    .split(" ")
    .map((token) => token.trim())
    .filter((token) => token.length >= 1);
}

function buildOrderFieldSearchValues(order, fieldKey) {
  if (fieldKey === "delivered_by_driver_name") {
    const summary = resolveOrderDeliveredByDriverSummary(order);
    return summary.names.map((name) => normalizeSearchText(name)).filter(Boolean);
  }

  const rawValue = order?.[fieldKey];
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return [];
  }

  if (fieldKey === DELIVERY_DATE_FIELD_KEY) {
    const displayValue = formatDateDisplay(rawValue);
    const isoValue = typeof rawValue === "string" ? rawValue.trim().slice(0, 10) : "";
    return [
      normalizeSearchText(displayValue),
      normalizeSearchText(isoValue),
      normalizeSearchText(displayValue.replace(/\s.*/, "")),
    ].filter(Boolean);
  }

  if (fieldKey === QUANTITY_FIELD_KEY) {
    const formatted = formatQuantityTons(rawValue);
    return [
      normalizeSearchText(rawValue),
      normalizeSearchText(formatted),
      normalizeNumericToken(rawValue),
    ].filter(Boolean);
  }

  return [normalizeSearchText(rawValue)].filter(Boolean);
}

function tokenMatchesField(token, fieldKey, fieldValues) {
  if (!fieldValues.length) {
    return false;
  }

  const normalizedToken = normalizeSearchText(token);
  const numericToken = normalizeNumericToken(token);

  if (fieldKey === ORDER_ID_FIELD_KEY) {
    return fieldValues.some((fieldValue) => fieldValue.startsWith(normalizedToken) || fieldValue.includes(normalizedToken));
  }

  if (fieldKey === QUANTITY_FIELD_KEY && numericToken) {
    return fieldValues.some((fieldValue) => fieldValue === numericToken || fieldValue.includes(normalizedToken));
  }

  if (fieldKey === DELIVERY_DATE_FIELD_KEY) {
    return fieldValues.some((fieldValue) => fieldValue.includes(normalizedToken));
  }

  return fieldValues.some((fieldValue) => fieldValue.includes(normalizedToken));
}

export function tokenMatchesOrder(token, order) {
  return ORDER_SEARCH_FIELD_KEYS.some((fieldKey) => tokenMatchesField(
    token,
    fieldKey,
    buildOrderFieldSearchValues(order, fieldKey),
  ));
}

export function matchOrderSearch(order, query) {
  const activeQuery = resolveActiveOrderSearchQuery(query);
  const tokens = tokenizeQuery(activeQuery);
  if (tokens.length === 0) {
    return true;
  }

  return tokens.every((token) => tokenMatchesOrder(token, order));
}

export function getOrderFieldDisplayValue(order, fieldKey) {
  if (!order || !fieldKey) {
    return "";
  }

  if (fieldKey === DELIVERY_DATE_FIELD_KEY) {
    const display = formatDateDisplay(order[fieldKey]);
    return display === "—" ? "" : display;
  }

  if (fieldKey === QUANTITY_FIELD_KEY) {
    const value = order[fieldKey];
    return value === null || value === undefined || value === "" ? "" : formatQuantityTons(value);
  }

  const rawValue = order[fieldKey];
  return rawValue === null || rawValue === undefined ? "" : String(rawValue);
}

export function parseAdvancedQuery(query) {
  const normalizedQuery = typeof query === "string" ? query.trim() : "";
  if (!normalizedQuery) {
    return { plainText: "", fieldTokens: {} };
  }

  return {
    plainText: normalizedQuery,
    fieldTokens: {},
  };
}

export const EMPTY_ORDER_ADVANCED_FILTERS = Object.freeze({
  ...Object.fromEntries(
    ORDER_ADVANCED_FILTER_CATALOG.flatMap((filter) => (
      filter.type === "number-range" || filter.type === "date-range"
        ? [[`${filter.key}_min`, ""], [`${filter.key}_max`, ""]]
        : [[filter.key, ""]]
    )),
  ),
  scope: "",
});

function normalizeBooleanFilterValue(value) {
  if (value === true || value === "oui" || value === "yes" || value === "1") {
    return "oui";
  }
  return "non";
}

function normalizeDeliveryPeriodFilterValue(value) {
  const normalizedValue = normalizeSearchText(value);
  return normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "oui"
    ? "evening"
    : "day";
}

function parseRangeBoundary(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const parsedValue = Number.parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsedValue) ? parsedValue : null;
}

function fieldValueMatchesTextFilter(order, filterKey, filterValue) {
  const normalizedFilterValue = normalizeSearchText(filterValue);
  if (!normalizedFilterValue) {
    return true;
  }

  if (filterKey === "delivered_by_driver_name") {
    return resolveOrderDeliveredByDriverSummary(order).names.some(
      (driverName) => normalizeSearchText(driverName).includes(normalizedFilterValue),
    );
  }

  return normalizeSearchText(getOrderReportFieldDisplayValue(order, filterKey)).includes(normalizedFilterValue);
}

function fieldValueMatchesEnumFilter(order, filter, filterValue) {
  if (!filterValue) {
    return true;
  }

  if (filter.key === "delivery_period") {
    return normalizeDeliveryPeriodFilterValue(order?.delivery_period) === filterValue;
  }

  return normalizeSearchText(getOrderReportFieldDisplayValue(order, filter.key)) === normalizeSearchText(filterValue);
}

function parseDateRangeBoundary(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const isoValue = toDateInputValue(value);
  return isoValue || null;
}

function fieldValueMatchesDateRangeFilter(order, filterKey, advancedFilters) {
  const minValue = parseDateRangeBoundary(advancedFilters?.[`${filterKey}_min`]);
  const maxValue = parseDateRangeBoundary(advancedFilters?.[`${filterKey}_max`]);
  if (!minValue && !maxValue) {
    return true;
  }

  const orderDate = toDateInputValue(order?.[filterKey]);
  if (!orderDate) {
    return false;
  }

  if (minValue && orderDate < minValue) {
    return false;
  }
  if (maxValue && orderDate > maxValue) {
    return false;
  }
  return true;
}

function fieldValueMatchesRangeFilter(order, filterKey, advancedFilters) {
  const minValue = parseRangeBoundary(advancedFilters?.[`${filterKey}_min`]);
  const maxValue = parseRangeBoundary(advancedFilters?.[`${filterKey}_max`]);
  if (minValue === null && maxValue === null) {
    return true;
  }

  const rawValue = Number.parseFloat(String(order?.[filterKey] ?? "").replace(",", "."));
  if (!Number.isFinite(rawValue)) {
    return false;
  }

  if (minValue !== null && rawValue < minValue) {
    return false;
  }
  if (maxValue !== null && rawValue > maxValue) {
    return false;
  }
  return true;
}

export function isOrderAdvancedFilterActive(filter, advancedFilters = {}) {
  if (!filter) {
    return false;
  }

  if (filter.type === "number-range" || filter.type === "date-range") {
    return Boolean(advancedFilters?.[`${filter.key}_min`] || advancedFilters?.[`${filter.key}_max`]);
  }

  return Boolean(advancedFilters?.[filter.key]);
}

export function buildOrderAdvancedFilterChips(advancedFilters = {}) {
  return ORDER_ADVANCED_FILTER_CATALOG.flatMap((filter) => {
    if (!isOrderAdvancedFilterActive(filter, advancedFilters)) {
      return [];
    }

    if (filter.type === "number-range") {
      const minValue = advancedFilters?.[`${filter.key}_min`] || "";
      const maxValue = advancedFilters?.[`${filter.key}_max`] || "";
      const value = minValue && maxValue
        ? `${minValue} à ${maxValue}`
        : minValue
          ? `min ${minValue}`
          : `max ${maxValue}`;
      return [{ key: filter.key, label: filter.label, value }];
    }

    if (filter.type === "date-range") {
      const minValue = advancedFilters?.[`${filter.key}_min`] || "";
      const maxValue = advancedFilters?.[`${filter.key}_max`] || "";
      const formattedMinValue = minValue ? formatDateDisplay(minValue) : "";
      const formattedMaxValue = maxValue ? formatDateDisplay(maxValue) : "";
      const value = formattedMinValue && formattedMaxValue
        ? `${formattedMinValue} à ${formattedMaxValue}`
        : formattedMinValue
          ? `à partir du ${formattedMinValue}`
          : `jusqu'au ${formattedMaxValue}`;
      return [{ key: filter.key, label: filter.label, value }];
    }

    const rawValue = advancedFilters?.[filter.key] || "";
    if (filter.type === "boolean") {
      return [{ key: filter.key, label: filter.label, value: rawValue === "oui" ? "Oui" : "Non" }];
    }

    if (filter.type === "enum") {
      const option = filter.options?.find((entry) => entry.value === rawValue);
      return [{ key: filter.key, label: filter.label, value: option?.label || rawValue }];
    }

    return [{ key: filter.key, label: filter.label, value: rawValue }];
  });
}

export function isDeliveryDateRangeFilterActive(advancedFilters = {}) {
  return Boolean(advancedFilters?.delivery_date_min || advancedFilters?.delivery_date_max);
}

export function matchOrderAdvancedFilters(order, advancedFilters = {}) {
  if (!order || !advancedFilters) {
    return true;
  }

  for (const filter of ORDER_ADVANCED_FILTER_CATALOG) {
    if (!isOrderAdvancedFilterActive(filter, advancedFilters)) {
      continue;
    }

    if (filter.type === "boolean") {
      if (normalizeBooleanFilterValue(order?.[filter.key]) !== advancedFilters[filter.key]) {
        return false;
      }
      continue;
    }

    if (filter.type === "enum") {
      if (!fieldValueMatchesEnumFilter(order, filter, advancedFilters[filter.key])) {
        return false;
      }
      continue;
    }

    if (filter.type === "number-range") {
      if (!fieldValueMatchesRangeFilter(order, filter.key, advancedFilters)) {
        return false;
      }
      continue;
    }

    if (filter.type === "date-range") {
      if (!fieldValueMatchesDateRangeFilter(order, filter.key, advancedFilters)) {
        return false;
      }
      continue;
    }

    if (!fieldValueMatchesTextFilter(order, filter.key, advancedFilters[filter.key])) {
      return false;
    }
  }

  return true;
}
